import uuid from "uuid";
import firebase from "firebase/app";
import "@firebase/auth";
import "@firebase/firestore";

import { Memo, Memos } from "../../models/memos";
import { Categories, Cat } from "../../models/categories";

const config = {
  apiKey: "AIzaSyDBTKHH7dmZzgZxXkGnKkR0tiw3Kx4YrO8",
  authDomain: "tabmemo-prod.firebaseapp.com",
  databaseURL: "https://tabmemo-prod.firebaseio.com",
  projectId: "tabmemo-prod",
  storageBucket: "tabmemo-prod.appspot.com",
  messagingSenderId: "972409630057",
  appId: "1:972409630057:web:0ac5fe7e2cea9480",
};

if (!firebase.apps.length) {
  firebase.initializeApp(config);
}

export default firebase;

export const loginCheck = async () =>
  new Promise((resolve: (user?: firebase.User | null) => void) =>
    firebase.auth().onAuthStateChanged(resolve)
  );

export async function firstLoginCheck() {
  const { currentUser } = firebase.auth();
  const userRef = firebase.firestore().collection("users").doc(currentUser.uid);
  const userDataInDB = await userRef.get().then(docs => docs.data());
  if (!userDataInDB && currentUser.email) {
    await userRef.set({ email: currentUser.email }, { merge: true });
  }
  //isFirstLogin
  return !Boolean(userDataInDB);
}

export async function loginWithGoogleInWeb() {
  firebase.auth().signInWithPopup(new firebase.auth.GoogleAuthProvider());
}

export async function loginWithGoogleInNative(provider) {
  try {
    if (firebase.auth().currentUser) {
      signOut();
      throw new Error("already login");
    }
    const result = await provider.logInAsync({
      expoClientId: "1:972409630057:web:0ac5fe7e2cea9480",
      iosClientId:
        "972409630057-aatp4d6p9e5sc39mbvb5009nh1ajdsjq.apps.googleusercontent.com",
      iosStandaloneAppClientId:
        "972409630057-44hsumgual2ih5lrajgae2g1luq7cebi.apps.googleusercontent.com",
      androidClientId:
        "972409630057-l8tlcbjvd5a5uue63et1tm0e1lcqb2ro.apps.googleusercontent.com",
      androidStandaloneAppClientId:
        "972409630057-130s2hrchkg2je3an5mg6aeu83l4m4do.apps.googleusercontent.com",
      scopes: ["profile", "email"],
    });

    if (result.type === "success") {
      const { idToken, accessToken } = result;
      const credential = firebase.auth.GoogleAuthProvider.credential(
        idToken,
        accessToken
      );
      await firebase.auth().signInWithCredential(credential);
      const isFirstLogin = await firstLoginCheck();
      return { currentUser: firebase.auth().currentUser, isFirstLogin };
    } else {
      throw new Error("login fail");
    }
  } catch (err) {
    return {
      currentUser: null,
      isFirstLogin: true,
    };
  }
}

export async function signOut() {
  return firebase.auth().signOut();
}

export async function getCollections(ref) {
  return ref.get().then(querySnapshot => querySnapshot);
}

export async function getAllDataFromColection(collectionId: string) {
  // console.log(ref);
  return new Promise((resolve, reject) => {
    firebase
      .firestore()
      .collection(collectionId)
      .get()
      .then(querySnapshot => {
        if (querySnapshot.empty) {
          resolve(null);
        }
        const returnData = {};
        querySnapshot.forEach(doc => {
          // console.log(doc.data());
          returnData[doc.id] = doc.data();
        });
        resolve(returnData);
      })
      .catch(err => {
        // console.log(err);
        reject(err);
      });
  });
}

export async function getDoc(collectionId: string, docId: string) {
  return firebase
    .firestore()
    .collection(collectionId)
    .doc(docId)
    .get()
    .then(docs => {
      if (docs.exists) {
        return docs.data();
      } else {
        return null;
      }
    });
}

export async function unsubscribeUser() {
  return firebase.auth().currentUser.delete();
}

export async function uploadFileAsync(uri: string, uid: string) {
  const ext = uri.split(".").slice(-1)[0];
  const path = `images/${uid}/${uuid.v4()}.${ext}`;

  return new Promise(async (resolve, reject) => {
    const blob = await fetch(uri).then(response => response.blob());
    const ref = firebase.storage().ref(path);
    const unsubscribe = ref.put(blob).on(
      "state_changed",
      state => {},
      err => {
        unsubscribe();
        reject(err);
      },
      async () => {
        unsubscribe();
        const url = await ref.getDownloadURL();
        resolve(url);
      }
    );
  });
}

export async function saveMemoToFirebase({
  uid,
  memoId,
  memo,
}: {
  uid: string;
  memoId: string;
  memo: Memo;
}) {
  const saveMemo = {
    ...memo,
    _modifiedAt: new Date(),
  };
  if (memo._createdAt) {
    saveMemo._createdAt = new Date(memo._createdAt);
  }
  return firebase
    .firestore()
    .collection("users")
    .doc(uid)
    .collection("memos")
    .doc(memoId)
    .set(saveMemo, { merge: true });
}

export async function deleteMemoOnFirebase({
  uid,
  memoId,
}: {
  uid: string;
  memoId: string;
}) {
  return firebase
    .firestore()
    .collection("users")
    .doc(uid)
    .collection("memos")
    .doc(memoId)
    .delete()
    .then(() => {
      firebase
        .firestore()
        .collection("users")
        .doc(uid)
        .update({
          deletedMemos: firebase.firestore.FieldValue.arrayUnion(memoId),
        });
    });
}

export async function saveCatToFirebase({
  uid,
  catId,
  cat,
}: {
  uid: string;
  catId: string;
  cat: Cat;
}) {
  return firebase
    .firestore()
    .collection("users")
    .doc(uid)
    .collection("categories")
    .doc(catId)
    .set(cat, { merge: true });
}

export async function deleteCatOnFirebase({
  uid,
  catId,
}: {
  uid: string;
  catId: string;
}) {
  firebase
    .firestore()
    .collection("users")
    .doc(uid)
    .collection("categories")
    .doc(catId)
    .delete()
    .then(() => {
      firebase
        .firestore()
        .collection("users")
        .doc(uid)
        .update({
          deletedCategories: firebase.firestore.FieldValue.arrayUnion(catId),
        });
    });
  firebase
    .firestore()
    .collection("users")
    .doc(uid)
    .update({
      order: firebase.firestore.FieldValue.arrayRemove(catId),
    });
}

export async function saveOrderToFirebase({
  uid,
  order,
}: {
  uid: string;
  order: string[];
}) {
  return firebase
    .firestore()
    .collection("users")
    .doc(uid)
    .set({ order }, { merge: true });
}

export const allSaveToFirebase = ({
  uid,
  memos,
  categories,
}: {
  uid: string;
  memos: Memos;
  categories: Categories;
}) => {
  Object.keys(memos).map(memoId => {
    const memo = memos[memoId];
    return saveMemoToFirebase({ uid, memoId, memo });
  });
  Object.keys(categories.list).map(catId => {
    const cat: Cat = categories.list[catId];
    return saveCatToFirebase({ uid, catId, cat });
  });
  saveOrderToFirebase({ uid, order: categories.order });
};

export const getAllDataFromFirebase = async ({ uid }: { uid: string }) => {
  const {
    order,
    deletedCategories = null,
    deletedMemos = null,
  } = await getDoc("users", uid);
  const memosOnFirebase = await getAllDataFromColection(`users/${uid}/memos`);
  const categories = await getAllDataFromColection(`users/${uid}/categories`);
  return {
    memosOnFirebase,
    categoryList: categories,
    categoryOrder: order,
    deletedCategories,
    deletedMemos,
  };
};
